import { useQuery } from "@tanstack/react-query";
import { Badge } from "@tcg/ui";
import { useRealtime } from "@tcg/realtime/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { usePos, useRegister } from "@/app/context";
import { MoneyText } from "@/components/MoneyText";
import { call } from "@/lib/api";
import { POS_TOPICS } from "@/lib/sse";

export const salesKey = (shiftId: string | null | undefined) =>
  ["pos-sales", "list", shiftId ?? "none"] as const;

/**
 * 取引履歴 (T07-25): the sales of this register's open shift, newest first → tap one → the
 * receipt (reprint / e-mail / QR / return). Updates itself (sale changes on the register's stream).
 */
export function ReceiptHistory() {
  const { t, i18n } = useTranslation();
  const { api } = usePos();
  const register = useRegister();
  const navigate = useNavigate();
  const shiftId = register.data?.openShift?.shiftId ?? null;
  const sales = useQuery({
    queryKey: salesKey(shiftId),
    queryFn: () =>
      call(() =>
        api.GET("/v1/pos/orders", {
          params: { query: { ...(shiftId ? { shiftId } : {}), limit: 100 } },
        }),
      ),
    enabled: Boolean(shiftId),
  });
  useRealtime([POS_TOPICS.sale], () => void sales.refetch(), {
    onResync: () => void sales.refetch(),
  });
  const time = new Intl.DateTimeFormat(i18n.resolvedLanguage === "ja" ? "ja-JP" : "en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Tokyo",
  });
  const rows = sales.data?.data ?? [];
  return (
    <section aria-labelledby="history-title" className="flex h-full min-h-0 flex-col gap-3 p-4">
      <h1 id="history-title" className="text-2xl font-semibold">
        {t("receipt.history_title")}
      </h1>
      {rows.length === 0 && !sales.isLoading ? (
        <p className="text-muted-foreground">{t("receipt.history_empty")}</p>
      ) : null}
      <ul className="flex min-h-0 flex-col gap-2 overflow-y-auto">
        {rows.map((row) => (
          <li key={row.id}>
            <button
              type="button"
              onClick={() => void navigate(`/receipts/${row.id}`)}
              className="grid min-h-pos-touch w-full grid-cols-[5rem_1fr_auto] items-center gap-3 rounded-md border px-4 py-2 text-left hover:bg-accent"
            >
              <span className="tabular-nums text-muted-foreground">
                {time.format(new Date(row.completedAt))}
              </span>
              <span className="flex flex-wrap items-center gap-2">
                <span className="font-mono">{row.receiptNo ?? row.orderNumber}</span>
                {row.status === "voided" ? (
                  <Badge variant="problem">{t("receipt.voided")}</Badge>
                ) : null}
                {row.needsReview ? (
                  <Badge variant="pending">{t("receipt.needs_review")}</Badge>
                ) : null}
                <span className="text-sm text-muted-foreground">
                  {row.payments
                    .map((p) =>
                      t(
                        p.method === "cash"
                          ? "tender.method_cash"
                          : p.method === "store_credit"
                            ? "tender.method_credit"
                            : "tender.method_card",
                      ),
                    )
                    .join(" + ")}
                </span>
              </span>
              <MoneyText amount={row.total} size="lg" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
